// Store del tema visual (ADR-025, spec de diseño de Inicio).
//
// Tres temas intercambiables: 'dark' (Oscuro azul),
// 'light' (Claro, por defecto) y 'forest' (Verde bosque). El tema
// se aplica poniendo [data-theme] en <html>; los componentes solo
// usan tokens (bg-background, text-primary, accent, ...) que se
// resuelven vía CSS variables, así que cambiar de tema no toca nada
// del árbol de componentes.
//
// Se persiste en localStorage ('camadero-theme') para sobrevivir
// recargas y entre sesiones.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeName = 'dark' | 'light' | 'forest';

/** Color scheme de cada tema. `dark`/`forest` son temas oscuros
 *  (añadir la clase `.dark` para que las variantes dark: de
 *  Tailwind funcionen en pantallas que aún no han migrado a
 *  tokens, p.ej. RegistroDiario); `light` es claro. Mantener este
 *  mapa sincronizado con THEMES — antes se codificaba en duro
 *  dentro de `applyTheme` (review PR #12, suggestion 2). */
export const THEME_COLOR_SCHEME: Record<ThemeName, 'dark' | 'light'> = {
  dark: 'dark',
  forest: 'dark',
  light: 'light',
};

export const THEMES: ReadonlyArray<{
  id: ThemeName;
  label: string;
  description: string;
}> = [
  { id: 'dark', label: 'ajustes.tema_oscuro', description: 'ajustes.tema_oscuro_desc' },
  { id: 'light', label: 'ajustes.tema_claro', description: 'ajustes.tema_claro_desc' },
  { id: 'forest', label: 'ajustes.tema_bosque', description: 'ajustes.tema_bosque_desc' },
];

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'light',
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'camadero-theme',
      // Solo persistimos el valor; el applyTheme se hace en App.tsx
      // vía useEffect al arrancar y en cada cambio.
      partialize: (s) => ({ theme: s.theme }),
    },
  ),
);

/** Aplica el tema al <html>: atributo data-theme + clase .dark
 *  (la clase dark: de Tailwind la usan las pantallas que aún no
 *  migran a tokens, p.ej. RegistroDiario). */
export function applyTheme(theme: ThemeName) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.classList.toggle('dark', THEME_COLOR_SCHEME[theme] === 'dark');
}
